import React, { useState, useMemo, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import RecoveryLayout from '../../components/RecoveryLayout';
import axiosInstance from '../../utils/axiosInstance';

/* ----------------------------------------------------------------
   Excel column → payload field mapping
---------------------------------------------------------------- */
const COLUMN_ALIASES = {
  Student: ['Student'],
  Reg_ID: ['Reg. ID', 'Reg ID', 'RegID'],
  Batch: ['Batch'],
  Contact_No: ['Contact No', 'Contact_No'],
  Email: ['Email'],
  Course: ['Course'],
  Currency: ['Currency'],
  Total_Due: ['Total Due', 'Total_Due'],
  Min_Due_Date: ['Min Due Date', 'Min. Due Date', 'Min_Due_Date', 'Min. Due', 'Min Due'],
  Reg_Date: ['Reg. Date', 'Reg Date', 'Reg_Date'],
  No_of_Dues: ['No. of Dues', 'No of Dues', 'No_of_Dues'],
  Reg_Status: ['Reg. Status', 'Reg Status', 'Reg_Status'],
  Pmt_Status: ['Pmt. Status', 'Pmt Status', 'Pmt_Status'],
  Course_Fee: ['Course Fee', 'Course_Fee'],
  Total_Paid: ['Total Paid', 'Total_Paid'],
  Address: ['Address'],
  Last_Paid: ['Last Paid', 'Last_Paid'],
  Created_By: ['Created By', 'Created_By'],
};

const COLUMN_MAP = Object.entries(COLUMN_ALIASES).reduce((acc, [field, aliases]) => {
  aliases.forEach((alias) => {
    acc[alias] = field;
  });
  return acc;
}, {});

const TEMPLATE_HEADERS = [
  'Student', 'Reg. ID', 'Batch', 'Contact No', 'Email', 'Course', 'Currency',
  'Total Due', 'Min Due Date', 'Reg. Date', 'No. of Dues', 'Reg. Status',
  'Pmt. Status', 'Course Fee', 'Total Paid', 'Address', 'Last Paid', 'Created By',
];

const SAMPLE_ROW = [
  'John Doe', 'REG001', 'BAT/HND/2024', '0771234567', 'john@example.com',
  'HND-IT/U', 'LKR', '30000', '2024-01-15', '2024-01-01', '2',
  'Active', 'Pending', '150000', '120000', 'No 10, Main St', '2024-01-10', 'Admin',
];

const STATUS_STYLES = {
  pending:   { bg: '#f1f5f9', color: '#475569' },
  blocked:   { bg: '#fee2e2', color: '#991b1b' },
  uploading: { bg: '#fef3c7', color: '#92400e' },
  inserted:  { bg: '#dcfce7', color: '#166534' },
  updated:   { bg: '#dbeafe', color: '#1e40af' },
  error:     { bg: '#fee2e2', color: '#991b1b' },
  skipped:   { bg: '#f1f5f9', color: '#94a3b8' },
};

const sanitize = (v) => (v === undefined || v === null ? '' : String(v).trim());

// Normalized fallback map: strip dots/spaces/underscores, lowercase
// e.g. "Min. Due Date" → "minduedate" → matches "Min Due Date" → 'Min_Due_Date'
const COLUMN_MAP_NORM = {};
Object.entries(COLUMN_MAP).forEach(([k, v]) => {
  COLUMN_MAP_NORM[k.toLowerCase().replace(/[.\s_]+/g, '')] = v;
});

/* ----------------------------------------------------------------
   Parse a raw record (keyed by Excel headers) into a normalised row
---------------------------------------------------------------- */
const buildRow = (raw, rowNumber) => {
  const mapped = {};
  Object.entries(raw).forEach(([col, val]) => {
    const trimmed = col.trim();
    const field = COLUMN_MAP[col]
      || COLUMN_MAP[trimmed]
      || COLUMN_MAP_NORM[trimmed.toLowerCase().replace(/[.\s_]+/g, '')];
    if (field) mapped[field] = val;
  });

  const errors = [];
  if (!sanitize(mapped.Student) && !sanitize(mapped.Reg_ID)) {
    errors.push('Student name and Reg ID are both empty');
  }

  return {
    rowNumber,
    payload: mapped,
    preview: {
      Student:    sanitize(mapped.Student),
      Reg_ID:     sanitize(mapped.Reg_ID),
      Batch:      sanitize(mapped.Batch),
      Course:     sanitize(mapped.Course),
      Total_Due:  sanitize(mapped.Total_Due),
      Pmt_Status: sanitize(mapped.Pmt_Status),
    },
    errors,
    status: errors.length ? 'blocked' : 'pending',
    message: errors.length ? errors.join('; ') : '',
  };
};

const DONE_STATUSES = new Set(['inserted', 'updated', 'error', 'skipped']);

export default function UploadDues({ pageTitle, section }) {
  const fileInputRef = useRef(null);
  const [rows, setRows]           = useState([]);
  const [fileMeta, setFileMeta]   = useState(null);
  const [parsing, setParsing]     = useState(false);
  const [uploading, setUploading] = useState(false);

  const validRows    = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const blockedRows  = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);
  const doneCount    = useMemo(() => rows.filter((r) => DONE_STATUSES.has(r.status)).length, [rows]);
  const insertedCount = useMemo(() => rows.filter((r) => r.status === 'inserted').length, [rows]);
  const updatedCount  = useMemo(() => rows.filter((r) => r.status === 'updated').length, [rows]);
  const errorCount    = useMemo(() => rows.filter((r) => r.status === 'error').length, [rows]);
  const progress = validRows.length ? Math.round((doneCount / validRows.length) * 100) : 0;

  const patchRow = useCallback((rowNumber, updates) => {
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, ...updates } : r)));
  }, []);

  const reset = useCallback(() => {
    setRows([]);
    setFileMeta(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /* ---------- Template download ---------- */
  const handleDownloadTemplate = useCallback(() => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, SAMPLE_ROW]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dues Template');
    XLSX.writeFile(wb, 'dues_upload_template.xlsx');
  }, []);

  /* ---------- File parsing ---------- */
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      Swal.fire('Invalid file', 'Please upload an .xlsx, .xls or .csv file.', 'warning');
      return;
    }

    setParsing(true);
    setRows([]);
    setFileMeta({ name: file.name, size: (file.size / 1024).toFixed(1) + ' KB' });

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '' });
        const parsed = data.map((record, i) => buildRow(record, i + 1));
        setRows(parsed);
      } catch (err) {
        Swal.fire('Parse error', err.message || 'Could not read the file.', 'error');
        setFileMeta(null);
      } finally {
        setParsing(false);
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  /* ---------- Upload ---------- */
  const handleUpload = useCallback(async () => {
    if (validRows.length === 0) return;

    const confirm = await Swal.fire({
      title: 'Upload Dues',
      html: `Upload <b>${validRows.length}</b> row(s)?<br/><small style="color:#64748b">Report month is auto-detected from each row's Min. Due Date.<br/>Same Reg ID for the same month will be updated; other months are untouched.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Upload',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;

    setUploading(true);

    // Mark all valid rows as uploading
    setRows((prev) =>
      prev.map((r) => (r.errors.length === 0 ? { ...r, status: 'uploading', message: '' } : r))
    );

    try {
      const payloads = validRows.map((r) => r.payload);
      const resp = await axiosInstance.post('/admin/recovery-dues/bulk-upload', { rows: payloads });
      const { results = [] } = resp.data || {};

      results.forEach((res) => {
        patchRow(res.rowNum, {
          status: res.status,
          message: res.message || (res.status === 'inserted' ? 'Inserted' : res.status === 'updated' ? 'Updated' : ''),
        });
      });

      Swal.fire({
        title: 'Upload complete',
        html: `<b>${resp.data.inserted}</b> inserted &nbsp;·&nbsp; <b>${resp.data.updated}</b> updated &nbsp;·&nbsp; <b>${resp.data.errors}</b> errors`,
        icon: resp.data.errors > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      Swal.fire('Upload failed', err?.response?.data?.message || err.message, 'error');
      setRows((prev) =>
        prev.map((r) => (r.status === 'uploading' ? { ...r, status: 'error', message: 'Request failed' } : r))
      );
    } finally {
      setUploading(false);
    }
  }, [validRows, patchRow]);

  /* ---------- Render ---------- */
  return (
    <RecoveryLayout title={pageTitle || 'Upload Dues'} currentSection={section}>

      {/* Top action bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Bulk Upload Dues</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Upload an Excel/CSV file to insert or update student due records.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleDownloadTemplate}
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }}
          >
            ↓ Download Template
          </button>
          <a
            href="/recovery/dues"
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            ← Back to Dues List
          </a>
        </div>
      </div>

      {/* Info banner */}
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 16 }}>ℹ️</span>
        <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
          <strong>Report month is auto-detected from each row's Min. Due Date.</strong><br />
          Rows with the same Reg ID &amp; same month will be <em>updated</em>; rows from a different month create a new monthly snapshot.
          Rows missing a Min. Due Date will be skipped.
        </p>
      </div>

      {/* File drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed #cbd5e1',
          borderRadius: 12,
          padding: '36px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: '#f8fafc',
          marginBottom: 24,
          transition: 'border-color 0.2s',
        }}
        onMouseOver={(e) => (e.currentTarget.style.borderColor = '#dc2626')}
        onMouseOut={(e) => (e.currentTarget.style.borderColor = '#cbd5e1')}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#334155' }}>
          Click to select an Excel or CSV file
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#94a3b8' }}>
          Supported: .xlsx · .xls · .csv &nbsp;|&nbsp; Max 2,000 rows
        </p>
        {fileMeta && (
          <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
            {fileMeta.name} &nbsp;({fileMeta.size})
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {parsing && (
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>Parsing file…</p>
      )}

      {/* Summary bar */}
      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Total Rows',  value: rows.length,        color: '#0f172a' },
            { label: 'Valid',       value: validRows.length,   color: '#16a34a' },
            { label: 'Blocked',     value: blockedRows.length, color: '#dc2626' },
            { label: 'Inserted',    value: insertedCount,      color: '#2563eb' },
            { label: 'Updated',     value: updatedCount,       color: '#7c3aed' },
            { label: 'Errors',      value: errorCount,         color: '#dc2626' },
          ].map((s) => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', minWidth: 100, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}

          {/* Progress bar */}
          {uploading && (
            <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, background: '#e2e8f0', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, background: '#dc2626', transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>{progress}%</span>
            </div>
          )}
        </div>
      )}

      {/* Upload button */}
      {validRows.length > 0 && !uploading && (
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={handleUpload}
            style={{
              padding: '10px 28px',
              borderRadius: 8,
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
          >
            Upload {validRows.length} Row{validRows.length !== 1 ? 's' : ''}
          </button>
          {rows.length > 0 && (
            <button
              onClick={reset}
              style={{ marginLeft: 10, padding: '10px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Preview table */}
      {rows.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['#', 'Student', 'Reg. ID', 'Batch', 'Course', 'Total Due', 'Pmt. Status', 'Status', 'Message'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const style = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
                  return (
                    <tr key={row.rowNumber} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{row.rowNumber}</td>
                      <td style={{ padding: '10px 14px', color: '#0f172a', fontWeight: 500 }}>{row.preview.Student || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#334155' }}>{row.preview.Reg_ID || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#334155' }}>{row.preview.Batch || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#334155' }}>{row.preview.Course || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#334155' }}>{row.preview.Total_Due || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#334155' }}>{row.preview.Pmt_Status || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700, background: style.bg, color: style.color, textTransform: 'capitalize' }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12 }}>{row.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </RecoveryLayout>
  );
}
